// Opportunities API service
// Handles fetch, upsert, and approve operations.
// HTTP, envelope unwrapping and ApiError mapping come from the shared @startus/lib client.

import type {
  Opportunity,
  OpportunityQueryParams,
  UpsertOpportunityPayload,
  UpsertOpportunityResponse,
  OpportunityAdmin,
  PaginatedResponse,
} from './types';
import { apiRequest, apiRequestData, apiRequestPaginated } from '@startus/lib';

// Re-export shared ApiError so hooks that import from this file keep working
export type { ApiError } from '@startus/lib';

// GET /api/v1/opportunities/:id — single opportunity, same role-based visibility as getOpportunities
export async function getOpportunity(id: string): Promise<Opportunity> {
  return apiRequestData<Opportunity>(`/api/v1/opportunities/${id}`);
}

// GET /api/v1/opportunities — returns paginated results filtered server-side by role + params
export async function getOpportunities(
  params: OpportunityQueryParams = {}
): Promise<PaginatedResponse<Opportunity>> {
  const {
    page = 1,
    limit = 10,
    search,
    status,
    type,
    regions,
    industries,
    eligibility,
    activeOnly,
    sortBy,
    sortOrder,
  } = params;
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) qs.set('search', search);
  if (status) qs.set('status', status);
  if (type) qs.set('type', type);
  if (regions?.length) qs.set('regions', regions.join(','));
  if (industries?.length) qs.set('industries', industries.join(','));
  if (eligibility?.length) qs.set('eligibility', eligibility.join(','));
  if (activeOnly) qs.set('activeOnly', 'true');
  if (sortBy) qs.set('orderBy', sortBy);
  if (sortOrder) qs.set('sortDir', sortOrder);
  return apiRequestPaginated<Opportunity>(`/api/v1/opportunities?${qs}`);
}

// POST /api/v1/opportunities — upserts by link (creates if new, updates if link exists)
export async function upsertOpportunity(
  payload: UpsertOpportunityPayload
): Promise<UpsertOpportunityResponse> {
  return apiRequestData<UpsertOpportunityResponse>('/api/v1/opportunities', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// PATCH /api/v1/opportunities/:id — admin only, updates an existing opportunity by id
export async function updateOpportunity(
  id: string,
  payload: UpsertOpportunityPayload
): Promise<UpsertOpportunityResponse> {
  return apiRequestData<UpsertOpportunityResponse>(`/api/v1/opportunities/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

// PATCH /api/v1/opportunities/:id/approve — admin only, sets status=APPROVED
export async function approveOpportunity(id: string): Promise<OpportunityAdmin> {
  return apiRequestData<OpportunityAdmin>(`/api/v1/opportunities/${id}/approve`, {
    method: 'PATCH',
  });
}

// DELETE /api/v1/opportunities/:id — admin only, permanently removes the opportunity
export async function deleteOpportunity(id: string): Promise<void> {
  await apiRequest(`/api/v1/opportunities/${id}`, { method: 'DELETE' });
}

export const opportunitiesService = {
  getAll: getOpportunities,
  getOne: getOpportunity,
  upsert: upsertOpportunity,
  update: updateOpportunity,
  approve: approveOpportunity,
  delete: deleteOpportunity,
};
